import { makeObservable, observable } from "mobx";

import { ReactHtmlParser } from "@use/react-html-parser";

import { BlockData, BlockModel, BlockRemoteData } from "@courses-models/course/src/ts/models/BlockModel";
import { getStandardPageCount, getTextContent } from "@courses-models/course/src/ts/utils";

/**
 * Reference to the html parser used to safely parse content entered by the
 * editor within the available richtext editor.
 */
const htmlParser = new ReactHtmlParser();

// Allow only the following list of tags
htmlParser.whitelistTags(
    "b", "strong", "i", "em", "u", "underline", "sup", "sub", "ol", "ul", "li", "a", "h1", "h2", "h3", "h4", "h5", "h6",
    "div", "p", "span", "br",
);

// Only allow html attributes relevant to anchor links on elements entered
htmlParser.whitelistAttrs("href", "target");

// Make sure that text nodes are wrapped in span tags
htmlParser.wrapTextNodes("span");

/**
 * Specification of raw data required to create a new RichtextBlockModel.
 */
type RichtextBlockData = BlockData & {
    content: string;
};

/**
 * Specification of the format used when serializing richtext block content for
 * storage in the remote backend.
 */
type RichtextBlockRemoteData = {
    content: string;
};

/**
 * Richtext blocks basically contain an html string, which represents the
 * content entered by the teacher in the richtexteditor.
 */
export class RichtextBlockModel extends BlockModel {
    /**
     * Contains the html content created by the teacher in the richtexteditor.
     */
    public content: string;

    /**
     * Apply initial data when the model is instantiated.
     */
    constructor(initial: RichtextBlockData) {
        super(initial);

        this.content = initial.content;

        makeObservable(this, {
            title: observable,
            note: observable,
            content: observable
        });
    }

    /**
     * Helper that'll create an instance of the LinkBlockModel based on a
     * serialized data from the remote backend.
     */
    public static DESERIALIZE(data: BlockRemoteData): RichtextBlockModel {
        const content = <RichtextBlockRemoteData>JSON.parse(data.content);

        return new RichtextBlockModel({
            id: data.id,
            note: {
                content: data.note.content,
                enabled: data.note.isVisible,
            },
            title: data.title,
            ...content,
        });
    }

    /**
     * Helper that'll transform the rich text content into a React-friendly
     * element.
     */
    public async getContent(): Promise<React.ReactNode[]> {
        return htmlParser.parse(this.content);
    }

    /**
     * Calculate the number of standard pages within this text page.
     */
    public getStandardPageCount(): number {
        return getStandardPageCount(getTextContent(this.content));
    }

    /**
     * Helper that'll return a serialized version of the content, so that it can
     * be stored remotely.
     */
    public serializeContent(): string {
        return JSON.stringify({ content: this.content });
    }

    /**
     * Helper that'll generate a snapshot of the content within this block
     */
    protected getContentSnapshot(): string {
        return this.content;
    }
}
